import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { HandHeart } from "lucide-react";
import { useShop } from "../context/ShopContext";

const STORY_IMAGE =
  "https://images.unsplash.com/photo-1615464670798-6e92fafa2a89?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxmZW1hbGUlMjBhcmNoaXRlY3QlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODczNTA1OTJ8MA&ixlib=rb-4.1.0&q=85";

const BrandStory = () => {
  const { t, lang, cval, contentImage } = useShop();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      id="story"
      ref={ref}
      className="py-24 md:py-36 px-6 md:px-12 max-w-[1440px] mx-auto"
      data-testid="story-section"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <div className="overflow-hidden aspect-[4/5]">
            <motion.img
              src={contentImage("story") || STORY_IMAGE}
              alt="VLP Dolap atölyesinde kadın girişimci"
              loading="lazy"
              style={{ y: imgY }}
              className="w-full h-[115%] object-cover"
            />
          </div>
          <div className="absolute -bottom-6 -right-4 md:-right-8 bg-olive text-cream px-6 py-4 flex items-center gap-3">
            <HandHeart size={22} strokeWidth={1.5} />
            <span className="font-serif text-lg italic" data-testid="story-badge">
              {t.story.badge}
            </span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-xs tracking-[0.3em] uppercase font-semibold text-olive-dark mb-4">
            {t.story.chapter} — {t.story.overline}
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight text-brown">
            {cval(`story_title_${lang}`, t.story.title)}
          </h2>
          <p className="mt-8 text-base font-light leading-relaxed text-brown-light">
            {cval(`story_p1_${lang}`, t.story.p1)}
          </p>
          <p className="mt-5 text-base font-light leading-relaxed text-brown-light">
            {cval(`story_p2_${lang}`, t.story.p2)}
          </p>

          <div className="mt-12 grid grid-cols-2 gap-6 border-t border-brown/10 pt-10">
            {t.story.stats.map((stat) => (
              <div key={stat.label} data-testid={`story-stat-${stat.label}`}>
                <p className="font-serif text-2xl sm:text-3xl font-medium text-olive-dark">
                  {stat.value}
                </p>
                <p className="mt-2 text-xs tracking-wide uppercase text-brown-muted leading-snug">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BrandStory;
